/**
 * Audio Manager - handles all game audio (music and sound effects)
 * Uses Web Audio API for playback with crossfade support
 */

export interface AudioSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
}

export type SFXType =
  | 'click'
  | 'build_complete'
  | 'unit_trained'
  | 'sword_hit'
  | 'arrow_shot'
  | 'spell_cast'
  | 'unit_death'
  | 'victory'
  | 'defeat'
  | 'notification';

export type MusicType = 'menu' | 'peaceful' | 'battle' | 'victory' | 'defeat';

const DEFAULT_SETTINGS: AudioSettings = {
  masterVolume: 0.7,
  musicVolume: 0.5,
  sfxVolume: 0.8,
  muted: false,
};

// File paths for music tracks
const MUSIC_FILES: Record<MusicType, string> = {
  menu: '/audio/music/tread_of_war.mp3',
  peaceful: '/audio/music/determined_pursuit.ogg',
  battle: '/audio/music/battle.ogg',
  victory: '/audio/music/victory.mp3',
  defeat: '/audio/music/defeat.mp3',
};

// File paths for sound effects
const SFX_FILES: Record<SFXType, string> = {
  click: '/audio/sfx/click.ogg',
  build_complete: '/audio/sfx/build_complete.ogg',
  unit_trained: '/audio/sfx/unit_trained.ogg',
  sword_hit: '/audio/sfx/sword_hit.ogg',
  arrow_shot: '/audio/sfx/arrow_shot.ogg',
  spell_cast: '/audio/sfx/spell_cast.ogg',
  unit_death: '/audio/sfx/unit_death.ogg',
  victory: '/audio/sfx/victory.ogg',
  defeat: '/audio/sfx/defeat.ogg',
  notification: '/audio/sfx/notification.ogg',
};

const FADE_DURATION = 2; // seconds

export class AudioManager {
  private settings: AudioSettings;
  private audioContext: AudioContext | null = null;
  private currentMusic: MusicType | null = null;
  private musicGainNode: GainNode | null = null;
  private sfxGainNode: GainNode | null = null;
  private initialized: boolean = false;

  // Audio buffers cache
  private sfxBuffers: Map<SFXType, AudioBuffer> = new Map();
  private musicBuffers: Map<MusicType, AudioBuffer> = new Map();

  // Current music source for stopping/crossfading
  private currentMusicSource: AudioBufferSourceNode | null = null;
  private currentMusicGain: GainNode | null = null;

  constructor() {
    this.settings = { ...DEFAULT_SETTINGS };
    this.loadSettings();
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      this.audioContext = new AudioContext();

      // Create gain nodes for volume control
      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.connect(this.audioContext.destination);
      this.musicGainNode.gain.value = this.settings.musicVolume * this.settings.masterVolume;

      this.sfxGainNode = this.audioContext.createGain();
      this.sfxGainNode.connect(this.audioContext.destination);
      this.sfxGainNode.gain.value = this.settings.sfxVolume * this.settings.masterVolume;

      this.initialized = true;
      console.log('Audio system initialized');

      // Preload audio files in background
      this.preloadAll();
    } catch (error) {
      console.warn('Audio initialization failed:', error);
    }
  }

  /**
   * Preload all audio files
   */
  private async preloadAll(): Promise<void> {
    // Preload SFX (small files, load all)
    const sfxPromises = (Object.keys(SFX_FILES) as SFXType[]).map(async (type) => {
      try {
        const buffer = await this.fetchAudioBuffer(SFX_FILES[type]);
        if (buffer) this.sfxBuffers.set(type, buffer);
      } catch (e) {
        console.warn(`Failed to load SFX: ${type}`, e);
      }
    });

    // Preload music (larger files, load all for smooth transitions)
    const musicPromises = (Object.keys(MUSIC_FILES) as MusicType[]).map(async (type) => {
      try {
        const buffer = await this.fetchAudioBuffer(MUSIC_FILES[type]);
        if (buffer) this.musicBuffers.set(type, buffer);
      } catch (e) {
        console.warn(`Failed to load music: ${type}`, e);
      }
    });

    await Promise.all([...sfxPromises, ...musicPromises]);
    console.log(`Audio loaded: ${this.sfxBuffers.size} SFX, ${this.musicBuffers.size} music tracks`);
  }

  /**
   * Fetch and decode an audio file
   */
  private async fetchAudioBuffer(url: string): Promise<AudioBuffer | null> {
    if (!this.audioContext) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const arrayBuffer = await response.arrayBuffer();
      return await this.audioContext.decodeAudioData(arrayBuffer);
    } catch (e) {
      console.warn(`Failed to fetch audio: ${url}`, e);
      return null;
    }
  }

  /**
   * Resume audio context if suspended (browser autoplay policy)
   */
  async resume(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Play a sound effect
   */
  playSFX(type: SFXType, volume: number = 1.0): void {
    if (!this.initialized || this.settings.muted) return;

    const buffer = this.sfxBuffers.get(type);
    if (buffer && this.audioContext && this.sfxGainNode) {
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;
      source.connect(gainNode);
      gainNode.connect(this.sfxGainNode);
      source.start(0);
    }
  }

  /**
   * Play a 3D positional sound effect
   */
  playSFX3D(type: SFXType, x: number, z: number, listenerX: number, listenerZ: number): void {
    if (!this.initialized || this.settings.muted) return;

    // Calculate distance-based volume
    const dx = x - listenerX;
    const dz = z - listenerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);
    const maxDistance = 50;
    const volume = Math.max(0, 1 - distance / maxDistance);

    if (volume > 0.1) {
      this.playSFX(type, volume);
    }
  }

  /**
   * Play background music with crossfade
   */
  playMusic(type: MusicType, fadeIn: boolean = true): void {
    if (!this.initialized) return;
    if (this.currentMusic === type) return;

    // Fade out current music
    if (this.currentMusicSource && this.currentMusicGain && this.audioContext) {
      const oldGain = this.currentMusicGain;
      const oldSource = this.currentMusicSource;
      if (fadeIn) {
        oldGain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + FADE_DURATION);
        setTimeout(() => {
          try { oldSource.stop(); } catch (_e) { /* already stopped */ }
        }, FADE_DURATION * 1000);
      } else {
        try { oldSource.stop(); } catch (_e) { /* already stopped */ }
      }
    }

    this.currentMusic = type;

    const buffer = this.musicBuffers.get(type);
    if (buffer && this.audioContext && this.musicGainNode) {
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      // Loop for menu, peaceful, battle; don't loop victory/defeat
      source.loop = type !== 'victory' && type !== 'defeat';

      const gainNode = this.audioContext.createGain();
      source.connect(gainNode);
      gainNode.connect(this.musicGainNode);

      if (fadeIn) {
        gainNode.gain.value = 0;
        gainNode.gain.linearRampToValueAtTime(1, this.audioContext.currentTime + FADE_DURATION);
      } else {
        gainNode.gain.value = 1;
      }

      source.start(0);
      this.currentMusicSource = source;
      this.currentMusicGain = gainNode;

      source.onended = () => {
        if (this.currentMusicSource === source) {
          this.currentMusicSource = null;
          this.currentMusicGain = null;
        }
      };
    }
  }

  /**
   * Stop current music
   */
  stopMusic(fadeOut: boolean = true): void {
    if (!this.currentMusic) return;

    if (this.currentMusicSource && this.currentMusicGain && this.audioContext) {
      if (fadeOut) {
        this.currentMusicGain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + FADE_DURATION);
        const source = this.currentMusicSource;
        setTimeout(() => {
          try { source.stop(); } catch (_e) { /* already stopped */ }
        }, FADE_DURATION * 1000);
      } else {
        try { this.currentMusicSource.stop(); } catch (_e) { /* already stopped */ }
      }
    }

    this.currentMusic = null;
    this.currentMusicSource = null;
    this.currentMusicGain = null;
  }

  /**
   * Set master volume
   */
  setMasterVolume(volume: number): void {
    this.settings.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateVolumes();
    this.saveSettings();
  }

  /**
   * Set music volume
   */
  setMusicVolume(volume: number): void {
    this.settings.musicVolume = Math.max(0, Math.min(1, volume));
    this.updateVolumes();
    this.saveSettings();
  }

  /**
   * Set SFX volume
   */
  setSFXVolume(volume: number): void {
    this.settings.sfxVolume = Math.max(0, Math.min(1, volume));
    this.updateVolumes();
    this.saveSettings();
  }

  /**
   * Toggle mute
   */
  setMuted(muted: boolean): void {
    this.settings.muted = muted;
    this.updateVolumes();
    this.saveSettings();
  }

  /**
   * Get current settings
   */
  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  private updateVolumes(): void {
    if (!this.initialized) return;

    const masterMultiplier = this.settings.muted ? 0 : this.settings.masterVolume;

    if (this.musicGainNode) {
      this.musicGainNode.gain.value = this.settings.musicVolume * masterMultiplier;
    }
    if (this.sfxGainNode) {
      this.sfxGainNode.gain.value = this.settings.sfxVolume * masterMultiplier;
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('oneRealm_audioSettings', JSON.stringify(this.settings));
    } catch (e) {
      console.warn('Failed to save audio settings:', e);
    }
  }

  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('oneRealm_audioSettings');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<AudioSettings>;
        this.settings = { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load audio settings:', e);
    }
  }

  dispose(): void {
    this.stopMusic(false);
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.sfxBuffers.clear();
    this.musicBuffers.clear();
    this.initialized = false;
  }
}

// Singleton instance
export const audioManager = new AudioManager();
